import { defineStyleConfig } from '@chakra-ui/react';

/**
 * `Kbd` — a keyboard key rendered as a key: `esc`, `Tab`, `⌘K`.
 *
 * The 2026-09 design gives these one treatment everywhere: GT Pressura Mono at
 * 11px, uppercase, on a parchment fill with NO border.
 *
 * It lives in the theme rather than in a component because Chakra already ships
 * `Kbd` and the app already renders it (the collection description's Esc /
 * ⌘Enter hints). An un-themed `Kbd` takes Chakra's stock baseStyle — a
 * `gray.100` fill with `borderWidth: 1px` and `borderBottomWidth: 3px` — which
 * is both a raw ramp step and exactly the hairline box that reads as a text
 * input rather than a key at this size. Styling the theme entry fixes every
 * call site at once, including ones nobody remembers to migrate.
 *
 * The borders are zeroed EXPLICITLY, not merely left unset: `extendTheme` deep
 * merges this config over Chakra's, so an unstated property keeps the stock
 * value.
 *
 * NOT for a keyboard shortcut inside a sentence — those are prose, and a
 * parchment chip mid-paragraph is noise. This is for a key rendered as an
 * affordance beside the control it operates.
 */
export const Kbd = defineStyleConfig({
  baseStyle: {
    fontFamily: 'mono',
    fontSize: '11px',
    fontWeight: 'normal',
    lineHeight: '1',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'text.muted',
    // `bg.surface` is the parchment the design draws these on, and since the
    // 2026-09 frame flip that token is parchment rather than white, so a cap on
    // a white sheet reads without needing an outline.
    bg: 'bg.surface',
    borderRadius: 'lg',
    borderWidth: '0',
    borderBottomWidth: '0',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  sizes: {
    /** The compact form, for a cap standing on its own in a footer or a row. */
    inline: { px: 2, py: 1 },
    /**
     * Matches a 32px control row — full height, wider padding, so the cap sits
     * on the same baseline grid as the buttons beside it (the palette's `esc`).
     */
    row: { h: '32px', px: '10px', display: 'inline-flex', alignItems: 'center' },
  },
  defaultProps: { size: 'inline' },
});
